import { type SetStateAction, type Dispatch } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';
import { ArrowDownIcon, ArrowUpIcon } from '@dynatrace/strato-icons';

import {
  columnNameCSS,
  columnVisibilityCheckboxCSS,
  noItemsFoundCSS,
} from './DataTableV2ColumnsList.sty.js';
import { Checkbox } from '../../../../../forms/checkbox/Checkbox.js';
import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import { dataTableMessages } from '../../../../DataTableV2/messages.js';
import { useDataTableV2 } from '../../../contexts/DataTableV2Context.js';
import { getCanHide } from '../../../features/ColumnVisibility/ColumnVisibility.js';
import type { Column } from '../../../hooks/useTable/types.js';

// #region Typings
interface DataTableV2ColumnsListProps {
  visibilitySettings: boolean;
  columnOrderSettings: boolean;
  activeFilterValue: string;
  getVisibilityCheckboxState: (
    column: Column<unknown, unknown>,
  ) => boolean | 'indeterminate';
  updateLocalColumnVisibility: (
    column: Column<unknown, unknown>,
    currentVisibility: boolean | 'indeterminate',
  ) => void;
  localColumnOrder: string[];
  setLocalColumnOrder: Dispatch<SetStateAction<string[]>>;
}
// #endregion Typings

/* Helper function to better predicate matching in given filter */
function filterItem(item: string, predicate: string): boolean {
  const words = predicate.toLocaleLowerCase().split(' ');

  return words.reduce(
    (acc, curr) => item.toLocaleLowerCase().includes(curr) && acc,
    true,
  );
}

/**
 * Renders the ordered, filtered list of columns for the column settings modal.
 *
 * @internal
 */
export const DataTableV2ColumnsList = (props: DataTableV2ColumnsListProps) => {
  const {
    visibilitySettings,
    columnOrderSettings,
    activeFilterValue,
    getVisibilityCheckboxState,
    updateLocalColumnVisibility,
    localColumnOrder,
    setLocalColumnOrder,
  } = props;
  const table = useDataTableV2();
  const intl = useIntl();

  // order the flat columns using the local column order
  const orderedColumns = table
    .getAllFlatColumnsWithoutBuiltin()
    .sort((a, b) => {
      const idChildColumnA = a.columns.at(0)?.id;
      const idChildColumnB = b.columns.at(0)?.id;
      const isBChildOfA = !!a.columns.find((childCol) => childCol.id === b.id);
      const isAChildOfB = !!b.columns.find((childCol) => childCol.id === a.id);

      if (isBChildOfA) {
        return -1;
      } else if (isAChildOfB) {
        return 1;
      }

      return (
        localColumnOrder.indexOf(idChildColumnA ?? a.id) -
        localColumnOrder.indexOf(idChildColumnB ?? b.id)
      );
    });

  // filter the ordered columns using the active filter value
  const filteredColumns = orderedColumns.filter((column) => {
    if (column.columnDef.meta?.builtin === true) {
      return false;
    }
    const childColumns = column.columns;
    if (childColumns.length === 0) {
      const columnName =
        typeof column.columnDef.header === 'string'
          ? column.columnDef.header
          : column.id;

      return filterItem(columnName, activeFilterValue);
    } else {
      return childColumns.some((childColumn) => {
        const columnName =
          typeof childColumn.columnDef.header === 'string'
            ? childColumn.columnDef.header
            : childColumn.id;
        return filterItem(columnName, activeFilterValue);
      });
    }
  });

  // fallback message if no columns match the filter
  if (filteredColumns.length === 0) {
    return (
      <Text className={noItemsFoundCSS}>
        {intl.formatMessage(dataTableMessages.noItemsFound)}
      </Text>
    );
  }

  // extract only the leaf columns from the ordered flat columns
  // as column order only considers leaf columns
  const orderedLeafColumns = orderedColumns.flatMap((col) =>
    col.columns.length > 0 ? [] : col,
  );

  return filteredColumns.map((column) => {
    const currentVisibilityState = getVisibilityCheckboxState(column);
    const columnName =
      typeof column.columnDef.header === 'string'
        ? column.columnDef.header
        : column.id;
    // extract all children with the same parent as the current column
    // this is necessary to find out whether moving a column is possible
    const siblingColumns = orderedLeafColumns.flatMap((col) =>
      col.parent?.id === column.parent?.id ? col : [],
    );

    const canHide = getCanHide(table, column, (columns) =>
      columns.some((c) => c.columnDef.enableHiding),
    );

    const canMoveLeft = column.getCanMoveColumnLeft(
      orderedLeafColumns,
      siblingColumns,
    );
    const canMoveRight = column.getCanMoveColumnRight(
      orderedLeafColumns,
      siblingColumns,
    );

    return (
      <Flex
        key={column.id}
        justifyContent="space-between"
        alignItems="center"
        className={columnVisibilityCheckboxCSS({
          isChild: column.parent !== undefined,
        })}
      >
        {visibilitySettings ? (
          <Checkbox
            value={currentVisibilityState}
            aria-disabled={!canHide}
            onChange={() => {
              if (canHide) {
                updateLocalColumnVisibility(column, currentVisibilityState);
              }
            }}
            className={columnNameCSS}
          >
            {columnName}
          </Checkbox>
        ) : (
          <div className={columnNameCSS}>{columnName}</div>
        )}
        {table.options.enableColumnOrdering &&
          columnOrderSettings &&
          activeFilterValue.length === 0 && (
            <Flex>
              <Tooltip
                text={intl.formatMessage(dataTableMessages.moveColumnUpLabel)}
                placement="left"
              >
                <Button
                  size="condensed"
                  aria-label={intl.formatMessage(
                    dataTableMessages.moveColumnUpLabel,
                  )}
                  aria-disabled={!canMoveLeft}
                  onClick={() =>
                    column &&
                    canMoveLeft &&
                    setLocalColumnOrder(
                      column.moveColumnLeft(
                        orderedLeafColumns,
                        localColumnOrder,
                      ),
                    )
                  }
                >
                  <Button.Prefix>
                    <ArrowUpIcon />
                  </Button.Prefix>
                </Button>
              </Tooltip>
              <Tooltip
                text={intl.formatMessage(dataTableMessages.moveColumnDownLabel)}
                placement="right"
              >
                <Button
                  size="condensed"
                  aria-label={intl.formatMessage(
                    dataTableMessages.moveColumnDownLabel,
                  )}
                  aria-disabled={!canMoveRight}
                  onClick={() =>
                    canMoveRight &&
                    setLocalColumnOrder(
                      column.moveColumnRight(
                        orderedLeafColumns,
                        localColumnOrder,
                      ),
                    )
                  }
                >
                  <Button.Prefix>
                    <ArrowDownIcon />
                  </Button.Prefix>
                </Button>
              </Tooltip>
            </Flex>
          )}
      </Flex>
    );
  });
};
